import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import * as format from "date-fns/format";
import update from "immutability-helper";
import * as React from "react";
import Modal from "src/components/Modal";
import { Prompt, RouteComponentProps } from "react-router-dom";
import {
  ICoach,
  IDirectorParams,
  IResponseSummaryRequest,
  ISport,
  ISurveyComments,
  ISurveyQuestion,
  ISurveyWrittenResponseResponse,
  TemplateQuestionCategory,
} from "src/services";
import Component from "../../components/Component";
import Page from "../../components/Page";
import PartialCleanCheck from "../../components/PartialCleanCheck";
import withAxios, { IAxiosProps } from "../../components/withAxios";
import withRescroll from "../../components/withRescroll";
import { withSchoolContext } from "../DirectorPage";
import IDirectorPageProps from "./IDirectorPageProps";
import {
  getCurrentStudentNameMask,
  IStudentNameMaskParams,
} from "./administrators/studentNameMask";
import { formatSummaryHeadings } from "./summaryFormatting";
// import { CHAT_GPT_KEY } from "src/constants";
// const publicUrl = process.env.PUBLIC_URL;

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  button: {
    marginRight: theme.spacing(1),
  },
  categoryDiv: {
    width: "100%",
  },
  divider: {
    margin: theme.spacing(1),
  },
  lightGrey: {
    backgroundColor: "#f4f4f4",
  },
  paper: {
    marginBottom: "2em",
    padding: ".4em",
    width: "100%",
  },
  question: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  questions: {
    maxWidth: "100%",
    width: "1200px",
  },
  responses: {
    margin: theme.spacing(2),
  },
  responsesCount: {
    color: "grey",
  },
  summary: {
    resize: "none",
    width: "98%",
  },
  text: {
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap",
  },
});

interface ICommentsPageState {
  surveyComments: ISurveyComments;
  loadingSurveyComments?: true;
  puttingResponseSummaries?: true;
  pushingPage?: true;
  showStudentName: boolean;
  studentNameMaskSchool: boolean;
  studentNameMaskLoaded: boolean;
  loadingSummary?: boolean;
  summaries?: string[] | undefined;
  modalVisible: boolean;
  questionToDeleteIndex: number | null;
  summaryGenerated: boolean;
  lastGeneratedQuestionId: number | null;
  questionToDeleteId: number | null;
}
interface IChatGptRequest {
  coach_id: number;
  school_id: number;
  total_tokens: string;
  completion_tokens: string;
  prompt_tokens: string;
  chatgpttoken: string;
  chatgptRequest: string;
  chatgptResponse: string;
}

interface ICommentsPageProps {
  sport: ISport;
  coach: ICoach;
  surveyId: number;
  responseSource: number;
}

type IProps = ICommentsPageProps &
  RouteComponentProps &
  IDirectorPageProps &
  IAxiosProps &
  WithStyles<keyof ReturnType<typeof styles>>;

export default withSchoolContext<ICommentsPageProps>(
  withStyles(styles)(
    withRescroll(
      (location) => {
        // Any change means rescroll!
        return location.pathname;
      },
      withAxios(
        class CommentsPageClass extends Component<IProps, ICommentsPageState> {
          public readonly state = this.buildState({
            showStudentName: false,
            studentNameMaskSchool: false,
            studentNameMaskLoaded: false,
            surveyComments: {
              questions: [],
              responseSummaries: [],
              survey: {
                entryLevel: -1,
                id: -1,
                name: "",
                responseCollectionEndDate: -1,
                startDate: -1,
              },
            },
            loadingSummary: false,
            summaries: [],
            modalVisible: false,
            questionToDeleteIndex:null,
            questionToDeleteId: null,
            summaryGenerated: false,
            lastGeneratedQuestionId: null,
          });

          private partialCleanCheck: PartialCleanCheck<ICommentsPageState> =
            new PartialCleanCheck(this, ["surveyComments"]);

          private saveChatGptHistory = this.buildPost<IChatGptRequest, any>(
            this,
            "chatgpt/createChatGpt"
          );

          public readonly loadStudentNameMaskSetting = async () => {
            try {
              const { school } = this.props;
              this.setState({
                studentNameMaskSchool: await getCurrentStudentNameMask(
                  (params: IStudentNameMaskParams) => this.buildGetWithParams<any, IStudentNameMaskParams>(
                    this,
                    "coach-config/current"
                  )(params),
                  Number(school.id)
                ),
                studentNameMaskLoaded: true,
              });
            } catch (error) {
              // Fail closed so names are not exposed when the privacy setting cannot be checked.
              console.error("Failed to load student name masking setting", error);
              this.setState({
                studentNameMaskSchool: true,
                studentNameMaskLoaded: true,
              });
            }
          };

          public componentDidMount() {
            this.loadStudentNameMaskSetting();
            this.loadSurveyAndComments();
          }

          public render() {
            const {
              loadingSurveyComments,
              puttingResponseSummaries,
              surveyComments,
              pushingPage,
              loadingSummary,
              // summaries,
            } = this.state;
            const { coach, classes, sport, responseSource, school } = this.props;
            const survey = surveyComments.survey;
            // if (survey.responseCollectionEndDate === null) {
            //   return <span>Survey is still going</span>;
            // }
            return (
              <Page
                className={classes.lightGrey}
                header={`${school.isEmbeddable === true ? "" : ":"+ sport.name} ${format(
                  survey.startDate,
                  "MMMM D, YYYY"
                )} to ${format(
                  survey.responseCollectionEndDate ? survey.responseCollectionEndDate : new Date(),
                  "MMMM D, YYYY"
                )} ${coach.name}`}
                subtitle="Read through student (and parent) comments below and then make a quantitative comment for coaches."
              >
                <Prompt
                  when={
                    !this.partialCleanCheck.isClean() &&
                    pushingPage === undefined
                  }
                  message="You have unsaved changes. Are you sure you want to leave?"
                />
                {loadingSurveyComments && <LinearProgress variant="query" />}
                {loadingSummary && <LinearProgress variant="query" />}
                <div>
                  <div className={classes.questions}>
                    {responseSource === 1 /* Source = Student */ ? (
                      <>
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.expectations,
                          "Expectations",
                          responseSource
                        )}
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.warmth,
                          "Relational Warmth",
                          responseSource
                        )}
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.other,
                          "Written Response",
                          responseSource
                        )}
                      </>
                    ) : null}
                    {responseSource === 2 /* Source = Parent */ ? (
                      <>
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.expectations,
                          "Expectations",
                          responseSource
                        )}
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.warmth,
                          "Relational Warmth",
                          responseSource
                        )}
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.other,
                          "Written Response",
                          responseSource
                        )}
                        {this.renderQuestionsCategory(
                          TemplateQuestionCategory.parent,
                          "Parent",
                          responseSource
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
                {puttingResponseSummaries && <LinearProgress />}
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={this.updateResponseSummaries}
                >
                  {this.getNextPageIsParentComments()
                    ? "Parent Comments"
                    : "Save and View"}
                </Button>
              </Page>
            );
          }

          private getNextPageIsParentComments() {
            return (
              this.state.surveyComments.questions !== undefined &&
              this.state.surveyComments.questions.find(
                (q) => q.parentResponses.length !== 0
              ) &&
              this.props.responseSource !== 2
            );
          }

          private renderQuestionsCategory(
            category: number,
            title: string,
            responseSource: number
          ) {
            const { classes } = this.props;
            const { surveyComments, showStudentName, studentNameMaskSchool, studentNameMaskLoaded } = this.state;
            let sq = surveyComments.questions.filter(
              (sqc) => sqc.surveyQuestion.category === category
            );

            /* To avoid a written response from a difference source from showing up in for ex. the parent question 
    summaries--we must filter out the questions which have no responses. In other words, if a question 
    has no responses, it either has no responses and shouldn't be shown OR no question text was 
    associated with the given perspective and shouldn't be shown */
            if (responseSource === 1) {
              /* Source = Student */
              sq = sq.filter((sqc) => sqc.studentResponses.length !== 0);
            }
            if (responseSource === 2) {
              /* Source = Parent */
              sq = sq.filter((sqc) => sqc.parentResponses.length !== 0);
            }

            if (sq.length === 0) {
              return null;
            }
            return (
              <Grid className={classes.lightGrey} container={true}>
                <div className={classes.categoryDiv}>
                  <h2>{title}</h2>
                  {studentNameMaskLoaded && !studentNameMaskSchool && (
                    <span>
                      Show names{" "}
                      <Switch
                        checked={showStudentName}
                        onClick={this.showStudentNameChange}
                      />
                    </span>
                  )}
                  {sq.map((question) => this.renderQuestion(question))}
                </div>
              </Grid>
            );
          }

          private renderQuestion(question: {
            surveyQuestion: ISurveyQuestion;
            parentResponses: ISurveyWrittenResponseResponse[];
            studentResponses: ISurveyWrittenResponseResponse[];
            index: number;
          }) {
            const { surveyComments } = this.state;
            const { classes } = this.props;
            const isChampions101 =
              surveyComments.survey.entryLevel >= 1 &&
              surveyComments.survey.entryLevel <= 4;
            const { modalVisible, summaryGenerated, lastGeneratedQuestionId, questionToDeleteId } = this.state;
            const responseSummary = surveyComments.responseSummaries.find(
              (rs) => rs.surveyQuestionId === question.surveyQuestion.id
            );
            if (responseSummary === undefined) {
              return null;
            }
            return (
              <Grid key={question.surveyQuestion.id} item={true} xs={12}>
                <Paper className={classes.paper}>
                  {this.props.responseSource === 1 ? (
                    <>
                      <Typography variant="h5" className={classes.text}>
                        {question.surveyQuestion.studentText}
                      </Typography>
                      {this.renderResponses(question.studentResponses)}
                      <textarea
                        rows={6}
                        value={responseSummary.studentSummary}
                        onChange={this.responseSummaryChange(
                          question.index,
                          "student"
                        )}
                        className={classes.summary}
                      />
                    </>
                  ) : null}

                  {this.props.responseSource === 2 ? (
                    <>
                      <Typography variant="h5" className={classes.text}>
                        {question.surveyQuestion.parentText}
                      </Typography>
                      {this.renderResponses(question.parentResponses)}
                      <textarea
                        rows={6}
                        value={
                          responseSummary.parentSummary === null
                            ? ""
                            : responseSummary.parentSummary
                        }
                        onChange={this.responseSummaryChange(
                          question.index,
                          "parent"
                        )}
                        className={classes.summary}
                      />
                    </>
                  ) : null}
                  <div className={classes.buttonContainer}>
                    {!(isChampions101 && this.props.responseSource === 1) && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.generateSummary(question.surveyQuestion.id)}
                        className={classes.button}
                      >
                        {(summaryGenerated && lastGeneratedQuestionId === question.surveyQuestion.id &&
                          (responseSummary.parentSummary || responseSummary.studentSummary) ) ||
                          ((responseSummary as any)?.parentChat === false || (responseSummary as any)?.studentChat === false ) ?
                          "Regenerate" : "Generate"
                        }
                      </Button>
                    )}
                    {(responseSummary.parentSummary || responseSummary.studentSummary)
                       ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => this.saveSummary(surveyComments.responseSummaries)}
                        className={this.props.classes.button}
                      >
                        Save
                      </Button>
                    ) : null}
                    {(responseSummary.parentSummary || responseSummary.studentSummary)
                      ?
                      <Button
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        onClick={() => this.openDeleteModal(question.index, responseSummary.surveyQuestionId)}
                      >
                        Delete
                      </Button> : ""
                    }
                  </div>
                </Paper>
                <div className="kldksf">
                  <Modal className="modal-delete-summary" open={modalVisible} closeModal={this.closeModal}>
                    <Grid
                      style={{ height: "100%" }}
                      container = {true}
                      direction="column"
                      justifyContent="space-between"
                    >
                      <Grid>
                        <h2>Are you sure you want to delete this summary?</h2>
                        <span>This action cannot be undone.</span>
                      </Grid>
                      <Grid container={true} justifyContent="space-between">
                        <Button onClick={this.closeModal} variant="contained">
                          Cancel
                        </Button>
                        <Button
                           onClick={() => 
                            {
                              this.clearSummary(this.props.responseSource === 2 ? "parent" : "student",  questionToDeleteId ?? 0, 
                            responseSummary?.coachId,)
                            }}
                          variant="contained"
                          color="secondary"
                        >
                          Delete
                        </Button>
                      </Grid>
                    </Grid>
                  </Modal>
                </div>
              </Grid>
            );
          }

          private openDeleteModal = (index: number, id: number) => {
            this.setState({
              modalVisible: true, questionToDeleteIndex: index,
              questionToDeleteId: id
            });
          };

          private closeModal = () => {
            this.setState({ modalVisible: false, questionToDeleteIndex: null });
          };

          private renderResponses(responses: ISurveyWrittenResponseResponse[]) {
            const { classes } = this.props;
            const { showStudentName, studentNameMaskSchool } = this.state;
            const canShowStudentNames = showStudentName && !studentNameMaskSchool;
            return (
              <div>
                <Typography className={classes.responsesCount}>
                  Response count: {responses.length}
                </Typography>
                <div style={{overflowY: "auto", maxHeight: "300px"}}>
                  {responses.map((studentResponse) => (
                    <div
                      key={
                        studentResponse.surveyQuestionId +
                        studentResponse.surveyResponseId
                      }
                    >
                      <Divider className={classes.divider} />
                      <div className={`${classes.responses} ${classes.text}`}>
                        {canShowStudentNames ? (
                          <b style={{ fontSize: "0.65em", color: "grey" }}>
                            {studentResponse.name}
                          </b>
                        ) : null}
                        <Typography>{studentResponse.response}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          private showStudentNameChange = () => {
            if (this.state.studentNameMaskSchool) {
              return;
            }
            this.setState((oldState) => ({
              showStudentName: !oldState.showStudentName,
            }));
          };

          private responseSummaryChange =
            (i: number, source: string) =>
              (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                this.setState({ summaryGenerated: true });
                // set value here to make it turn to a value instead of pointer
                const value = e.target.value;
                const key = source + "Summary";
                const isChampionsStudentComments =
                  source === "student" &&
                  this.state.surveyComments.survey.entryLevel >= 1 &&
                  this.state.surveyComments.survey.entryLevel <= 4;
                this.setState((prevState) =>
                  update(prevState, {
                    surveyComments: {
                      responseSummaries: {
                        [i]: {
                          [key]: { $set: value },
                          ...(isChampionsStudentComments
                            ? { studentCommentsEdited: { $set: true } }
                            : {}),
                        },
                      },
                    },
                  })
                );
              };

          private readonly updateResponseSummaries = () =>
            void this.runCancellableStatefulAsync(
              this,
              {
                puttingResponseSummaries: true,
              },
              {
                puttingResponseSummaries: undefined,
              },
              async () => {
                // post all response summaries
                const { school, history, coach, sport } = this.props;
                const { surveyComments } = this.state;
                await this.buildPut<IResponseSummaryRequest, void>(
                  this,
                  `response/summary`
                )({
                  schoolId: school.id,
                  summaries: surveyComments.responseSummaries,
                });
                if (this.getNextPageIsParentComments()) {
                  this.setState({ pushingPage: true }, () => {
                    history.push(
                      `/${school.slug}/comments/${surveyComments.survey.id}/${sport.id}/${coach.id}/2`
                    );
                  });
                  return;
                }
                this.setState({ pushingPage: true }, () => {
                  history.push(
                    `/${school.slug}/evaluation/${surveyComments.survey.id}/${sport.id}/${coach.id}`
                  );
                });
                // I will now push them to the view Page
              },
              () => {
                this.setState({ puttingResponseSummaries: undefined });
                window.alert("Failed to post response summaries");
              }
            );

          // private responseSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          //   this.setState({ responseSummaries: e.target.value });
          // }
          private readonly loadSurveyAndComments = () =>
            void this.runCancellableStatefulAsync(
              this,
              {
                loadingSurveyComments: true,
              },
              {
                loadingSurveyComments: undefined,
              },
              async () => {
                const { school, sport, coach, surveyId } = this.props;
                const surveyComments = await this.buildGetWithParams<
                  ISurveyComments,
                  IDirectorParams
                >(
                  this,
                  `survey/comments/${surveyId}/${sport.id}/${coach.id}`
                )({
                  schoolSlug: school.slug,
                });
                this.setState((oldState) => {
                  const newState = { surveyComments };
                  this.partialCleanCheck.setOldFormData(newState);
                  return newState;
                });
              },
              () => {
                this.setState({ loadingSurveyComments: undefined });
                window.alert("Failed to load comments");
              }
            );

          private clearSummary = (source: string, surveyQuestionId: number, coachId: number) => {
            const deleteSummary = async () => {
              try {
                await this.buildPut<{ surveyQuestionId: number; coachId: number }, void>(
                  this,
                  `school/templateDeleteQuestion`
                )({
                  surveyQuestionId,
                  coachId,
                });

                // const key = source + "Summary";
                // const { questionToDeleteIndex } = this.state;
                this.loadSurveyAndComments();
                // Ensure the index is not null before updating the state
                // if (questionToDeleteIndex !== null) {
                //   this.setState((prevState) => {
                //     const updatedResponseSummaries = [...prevState.surveyComments.responseSummaries];
                //     updatedResponseSummaries[questionToDeleteIndex] = {
                //       ...updatedResponseSummaries[questionToDeleteIndex],
                //       [key]: "", // Clear the summary for the specified source (parent or student)
                //     };

                //     return {
                //       surveyComments: {
                //         ...prevState.surveyComments,
                //         responseSummaries: updatedResponseSummaries,
                //       },
                //     };
                //   });
                // }

                this.closeModal();
              } catch (error) {
                window.alert("Failed to delete summary.");
              }
            };
            deleteSummary();
          };

          private generateSummary = async (questionId: number) => {
            const { school, coach } = this.props;

            this.setState({ loadingSummary: true });
            try {
              const question = this.state.surveyComments.questions.find(q => q.surveyQuestion.id === questionId);

              if (!question) {
                console.error("Question not found");
                this.setState({ loadingSummary: false });
                return;
              }
              const isStudentSource = this.props.responseSource === 1;
              const comments: string[] = [
                ...question.studentResponses.map(response => response.response),
                ...question.parentResponses.map(response => response.response),
              ];

              console.log("Number of comments:", comments);
              const numberOfComments = comments.length;
              const bulletPoints = numberOfComments >= 9 ? 3 : 2;

              const numberedComments = comments.map((comment, index) => `${index + 1}. ${comment}`).join('\n');
              const prompt = `
              Based on the following question and comments, generate exactly ${bulletPoints} bullet point summaries. Each bullet must include a clear heading of 15 words or less followed by a concise summary.
              
              Your response must strictly follow this numbered list format:
              1. HEADING 1: Summary point in 15 words or less.
              2. HEADING 2: Summary point in 15 words or less.
              ${bulletPoints === 3 ? '3. HEADING 3: Summary point in 15 words or less.' : ''}
              
              Instructions:
              - Each bullet point must start with a clear and descriptive heading followed by the summary.
              - The summary must be concise and not exceed 15 words.
              - Ensure proper formatting, with no hyphens, asterisks, or unnecessary symbols.
              - Always include a heading for every bullet point.
              
              Question: ${isStudentSource ? question.surveyQuestion.studentText : question.surveyQuestion.parentText}
              
              Comments:
              ${numberedComments}
              `;
              
                           
              const response = await this.callChatGPT(prompt);

              if (response && response.choices && response.choices.length > 0) {
                const content = formatSummaryHeadings(
                  response.choices[0].message.content
                    .split("\n")
                    .map((line: string) => line.trim())
                    .filter((line: string) => line.length > 0)
                    .join("\n")
                );
                const responseSummary = this.state.surveyComments.responseSummaries.find(
                  (rs) => rs.surveyQuestionId === questionId
                );
                // let modifiedContent = content;
                // // Use regular expression to remove any dashes, numbers, or other unwanted characters
                // modifiedContent = modifiedContent
                //   .split("\n") // Split the content into lines
                //   .map((line: any) => line.trim()) // Trim any extra spaces at the start or end of the line
                //   .map((line: any) => line.replace(/^[\s*-]*\s?/, '● ')) // Replace anything before the bullet with '● '
                //   .join("\n"); // Join the lines back together
                // // Save the modified content in the state
                if (responseSummary) {
                  if (isStudentSource) {
                    responseSummary.studentSummary = content;
                  } else {
                    responseSummary.parentSummary = content;
                  }
                }

                const chatGptRequest: IChatGptRequest = {
                  coach_id: coach.id,
                  school_id: school.id,
                  total_tokens: response.usage.total_tokens.toString(),
                  completion_tokens: response.usage.completion_tokens.toString(),
                  prompt_tokens: response.usage.prompt_tokens.toString(),
                  chatgpttoken: response.id,
                  chatgptRequest: prompt,
                  chatgptResponse: content
                };
                // save the chat history
                await this.saveChatGptHistory(chatGptRequest);
                this.setState({ summaryGenerated: true, lastGeneratedQuestionId: questionId  });
              } else {
                console.error("Unexpected response structure", response);
              }
              this.setState({ loadingSummary: false });
            } catch (error) {
              console.error("Error generating summary:", error);
              this.setState({ loadingSummary: false });
            }
          };

          // private callChatGPT = async (prompt: string) => {
          //   console.log("prompt", prompt)

          //   const response = await fetch("https://api.openai.com/v1/chat/completions", {
          //     method: "POST",
          //     headers: {
          //       "Content-Type": "application/json",
          //       "Authorization": `Bearer ${CHAT_GPT_KEY}`,
          //     },
          //     body: JSON.stringify({
          //       model: "gpt-4.1-mini",
          //       messages: [{ role: "user", content: prompt }],
          //     }),
          //   });

          //   if (!response.ok) {
          //     throw new Error("Network response was not ok");
          //   }
          //   return await response.json();
          // };

//           private callChatGPT = async (prompt: string) => {
//   console.log("prompt", prompt);

//   const data = {
//     model: "gpt-4.1-mini", // change to "gpt-4o" if needed
//     temperature: 1,
//     messages: [
//       {
//         role: "system",
//         content: prompt
//       }
//     ]
//   };

//   console.log(data, "data");

//   // const response = await fetch(
//   //   "http://localhost:3000/api/chatgpt/openAI/completions",
//   //   {
//   //     method: "POST",
//   //     headers: {
//   //       "Content-Type": "application/json"
//   //     },
//   //     body: JSON.stringify(data)
//   //   }
//   // );
// const response = await this.buildPost<any, any>(
//       this,
//       "chatgpt/openAI/completions"
//     )(data);

  

//   if (!response.ok) {
//     throw new Error(`Network response was not ok (${response.status})`);
//   }

//   // ✅ Only call .json() once
//   const jsonData = await response.json();
//   console.log(jsonData, "parsed JSON response");

//   return jsonData;
// };

private callChatGPT = async (prompt: string) => {
  console.log("prompt1", prompt);

  const data = {
    model: "gpt-4.1-mini",
    messages: [
      {
        role: "system",
        content: prompt
      }
    ]
  };

  console.log(data, "data");

  // buildPost returns the parsed JSON, not a Response object
  const jsonData = await this.buildPost<any, any>(
    this,
    "chatgpt/openAI/completions"
  )(data);

  console.log(jsonData, "parsed JSON response");

  return jsonData;
};

          private saveSummary = async (responseSummary: any) => {
            try {
              this.setState((prevState) => {
                const updatedResponseSummaries = prevState.surveyComments.responseSummaries.map((summary) => {
                  if (summary.surveyQuestionId === responseSummary.surveyQuestionId) {
                    return { ...summary, ...responseSummary };
                  }
                  return summary;
                });
          
                return {
                  surveyComments: {
                    ...prevState.surveyComments,
                    responseSummaries: updatedResponseSummaries,
                  },
                };
              }, async () => {
                try {
                  // Directly call the API here
                  const { school } = this.props;
                  const { surveyComments } = this.state;
          
                  // Make the API call to post the updated response summaries
                  await this.buildPut<IResponseSummaryRequest, void>(
                    this,
                    `response/summary`
                  )({
                    schoolId: school.id,
                    summaries: surveyComments.responseSummaries,
                  });
        
                  window.alert("Saved successfully");
          
                } catch (apiError) {
                  console.error("Failed to post response summaries:", apiError);
                }
              });
            } catch (error) {
              console.error("Failed to save summary:", error);
            }
          };  
        }
      )
    )
  )
);



// WEBPACK FOOTER //
// ./src/pages/director/CommentsPage.tsx